import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Loader2, Search, ChevronUp, ChevronDown, X } from 'lucide-react'
import { apiService } from '@/service/api'
import { useTranslation } from '@/i18n'
import { usePreventWheelPropagation } from '@/hooks/usePreventWheelPropagation'
import PreviewMatchList from '@/components/PreviewSearch/PreviewMatchList'
import type { PreviewMatch } from '@/components/PreviewSearch/types'
import { findTextMatches } from '@/components/PreviewSearch/findTextMatches'

/** Hits marked in the transcript. One <mark> element each, so this is a real cap. */
const MAX_TRANSCRIPT_MATCHES = 500

const InterviewPreview = ({ docId }: { docId: string }) => {
  const { t } = useTranslation()
  const [content, setContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // FS-657: find in transcript, with every match listed at once.
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const activeMarkRef = useRef<HTMLElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Prevent wheel events from propagating to tldraw canvas
  const scrollRef = usePreventWheelPropagation()

  useEffect(() => {
    const fetchContent = async () => {
      if (!docId) return

      setLoading(true)
      try {
        const blob = await apiService.downloadDocument(docId)
        const text = await blob.text()
        setContent(text)
      } catch (err) {
        console.error('Failed to load interview content:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchContent()
  }, [docId])

  // Debounced: a transcript is one big in-memory string, and scanning it plus
  // building a <mark> per hit on every keystroke froze the card — typing a single
  // "e" into a long interview is tens of thousands of matches.
  const [debouncedQuery, setDebouncedQuery] = useState('')
  useEffect(() => {
    // Clearing is immediate: closing the search must unmark the transcript now,
    // not 200ms from now.
    if (!query.trim()) {
      setDebouncedQuery('')
      return
    }
    const timer = setTimeout(() => setDebouncedQuery(query), 200)
    return () => clearTimeout(timer)
  }, [query])

  const matches = useMemo(
    () => findTextMatches(content ?? '', debouncedQuery).slice(0, MAX_TRANSCRIPT_MATCHES),
    [content, debouncedQuery]
  )
  const truncated =
    !!debouncedQuery.trim() && matches.length === MAX_TRANSCRIPT_MATCHES

  const matchListItems = useMemo<PreviewMatch[]>(
    () =>
      matches.map((m) => ({
        id: `${m.index}`,
        locationLabel: t('board:preview_search.line_label', { line: m.line }),
        snippet: m.snippet,
      })),
    [matches, t]
  )

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  // The matches lag the query by the debounce, so Enter inside that window steps
  // through the OLD set and can leave the pointer past the end of the new one —
  // an impossible "2 of 1" with nothing marked. Clamp when the set changes.
  useEffect(() => {
    if (activeIndex >= matches.length && matches.length > 0) setActiveIndex(0)
  }, [matches.length, activeIndex])

  // Keep the active hit on screen, whether it was picked from the list or
  // stepped to with the chevrons.
  useEffect(() => {
    // Optional call: jsdom has no scrollIntoView.
    activeMarkRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
  }, [activeIndex, matches])

  const openSearch = useCallback(() => {
    setIsSearchOpen(true)
    setTimeout(() => searchInputRef.current?.focus(), 0)
  }, [])

  // Cmd+F / Ctrl+F while the pointer is inside this preview.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!((e.metaKey || e.ctrlKey) && e.key === 'f')) return
      // A keydown can be targeted at window/document, which `contains` rejects.
      const target = e.target
      if (!(target instanceof Node)) return
      if (!containerRef.current?.contains(target)) return
      e.preventDefault()
      openSearch()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [openSearch])

  const closeSearch = () => {
    setIsSearchOpen(false)
    setQuery('')
    setActiveIndex(0)
  }

  const step = (delta: number) => {
    if (matches.length === 0) return
    setActiveIndex((i) => (i + delta + matches.length) % matches.length)
  }

  // Split the transcript around the matches so every one of them is marked, not
  // just the one being viewed.
  const marked = useMemo(() => {
    const text = content ?? ''
    if (matches.length === 0) return text
    const out: ReactNode[] = []
    let cursor = 0
    // Offsets must come from the SAME query the matches were found with, or a
    // mid-typing render slices at the wrong end.
    const term = debouncedQuery.trim()
    matches.forEach((match, i) => {
      if (match.index > cursor) out.push(text.slice(cursor, match.index))
      const end = match.index + term.length
      const isActive = i === activeIndex
      out.push(
        <mark
          key={match.index}
          ref={isActive ? activeMarkRef : undefined}
          className={
            isActive
              ? 'bg-orange-300 dark:bg-orange-500/60 text-inherit'
              : 'bg-yellow-200 dark:bg-yellow-500/40 text-inherit'
          }
        >
          {text.slice(match.index, end)}
        </mark>
      )
      cursor = end
    })
    out.push(text.slice(cursor))
    return out
  }, [content, matches, activeIndex, debouncedQuery])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400 dark:text-gray-500" />
      </div>
    )
  }

  return (
    <div ref={containerRef} className="h-full flex flex-col min-h-0">
      {isSearchOpen && (
        <div
          className="flex items-center gap-2 p-2 border-b border-gray-200 dark:border-dark-border shrink-0"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Search className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" aria-hidden />
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                step(e.shiftKey ? -1 : 1)
              }
              if (e.key === 'Escape') closeSearch()
            }}
            placeholder={t('board:preview_search.find_in_transcript', {
              defaultValue: 'Find in transcript...',
            })}
            aria-label={t('board:preview_search.find_in_transcript', {
              defaultValue: 'Find in transcript...',
            })}
            className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-200 dark:border-dark-border rounded bg-white dark:bg-dark-card focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {query.trim() && (
            <span className="text-xs text-gray-500 dark:text-dark-text-muted whitespace-nowrap shrink-0">
              {matches.length > 0
                ? `${activeIndex + 1} of ${matches.length}`
                : t('board:preview_search.no_matches')}
            </span>
          )}
          <button
            type="button"
            onClick={() => step(-1)}
            disabled={matches.length === 0}
            className="p-1.5 bg-gray-100 dark:bg-dark-accent border rounded hover:bg-gray-200 dark:hover:bg-dark-muted disabled:opacity-50"
            aria-label={t('board:search.previous_match_title')}
          >
            <ChevronUp size={16} />
          </button>
          <button
            type="button"
            onClick={() => step(1)}
            disabled={matches.length === 0}
            className="p-1.5 bg-gray-100 dark:bg-dark-accent border rounded hover:bg-gray-200 dark:hover:bg-dark-muted disabled:opacity-50"
            aria-label={t('board:search.next_match_title')}
          >
            <ChevronDown size={16} />
          </button>
          <button
            type="button"
            onClick={closeSearch}
            className="p-1.5 text-gray-500 dark:text-dark-text-muted hover:bg-gray-100 dark:hover:bg-dark-accent rounded"
            aria-label={t('board:search.close')}
          >
            <X size={16} />
          </button>
        </div>
      )}
      <div className="flex-1 min-h-0 flex">
        {/* `relative` hosts the search button. It wraps the scroller rather than
            sitting on it because an absolute child of a scrolling box scrolls
            away with the transcript. */}
        <div className="relative flex-1 min-w-0 min-h-0 flex">
          {/* tabIndex: focusable so the board can move focus in here when the
              preview opens, which is what lets this surface claim Cmd+F (the
              handler is scoped to keydowns inside it). Also allows keyboard
              scrolling. Same as PdfJsViewer's scroll container. */}
          <div ref={scrollRef} tabIndex={0} className="flex-1 min-w-0 overflow-auto p-4 focus:outline-none">
            <pre className="font-mono whitespace-pre-wrap break-words text-sm">
              {content
                ? marked
                : t('board:document_preview.no_content_available', { defaultValue: 'No content available' })}
            </pre>
          </div>
          {/* Cmd+F was the only way in, so the search did not exist for anyone
              who did not already know about it. Hidden while the bar is open —
              the bar carries its own close. This preview has no toolbar to put
              it in, unlike the PDF and image cards. */}
          {!isSearchOpen && (
            <button
              type="button"
              onClick={openSearch}
              onPointerDown={(e) => e.stopPropagation()}
              className="absolute top-2 right-2 z-10 p-1.5 rounded border border-gray-200 dark:border-dark-border bg-white/90 dark:bg-dark-card/90 text-gray-600 dark:text-dark-text-muted shadow-sm hover:bg-gray-100 dark:hover:bg-dark-accent"
              aria-label={t('board:preview_search.search_transcript')}
              title={t('board:preview_search.search_transcript')}
            >
              <Search size={16} />
            </button>
          )}
        </div>
        {isSearchOpen && debouncedQuery.trim() && (
          <PreviewMatchList
            matches={matchListItems}
            activeIndex={activeIndex}
            query={debouncedQuery}
            onSelect={setActiveIndex}
            footnote={
              truncated
                ? t('board:preview_search.first_matches_only', { count: MAX_TRANSCRIPT_MATCHES })
                : undefined
            }
            className="w-[264px] shrink-0 border-l border-gray-200 dark:border-dark-border"
          />
        )}
      </div>
    </div>
  )
}

export default InterviewPreview
